// Portfolio account types — real French & US products + custom. Country-aware.
const L = (fr, en) => ({ fr, en });

export const RANGES = ["1M", "3M", "6M", "YTD", "1Y", "5Y", "ALL"];

const FR_TYPES = [
  { value: "cto", label: L("CTO (Compte-titres)", "CTO (Securities account)") },
  { value: "cto_pro", label: L("CTO Pro", "CTO Pro") },
  { value: "pea", label: L("PEA", "PEA") },
  { value: "pea_pme", label: L("PEA-PME", "PEA-PME") },
  { value: "assurance_vie", label: L("Assurance Vie", "Life insurance") },
  { value: "assurance_vie_pro", label: L("Assurance Vie Pro", "Life insurance (Pro)") },
  { value: "per", label: L("PER (Épargne retraite)", "PER (Retirement)") },
  { value: "livret_a", label: L("Livret A", "Livret A") },
  { value: "ldds", label: L("LDDS", "LDDS") },
  { value: "lep", label: L("LEP", "LEP") },
  { value: "pel", label: L("PEL / CEL", "PEL / CEL") },
  { value: "compte_courant", label: L("Compte courant", "Checking account") },
  { value: "compte_pro", label: L("Compte courant Pro", "Business checking") },
  { value: "other", label: L("Autre / produit bancaire", "Other / bank product") },
];

const US_TYPES = [
  { value: "brokerage", label: L("Brokerage (imposable)", "Brokerage (Taxable)") },
  { value: "roth_ira", label: L("Roth IRA", "Roth IRA") },
  { value: "traditional_ira", label: L("Traditional IRA", "Traditional IRA") },
  { value: "401k", label: L("401(k)", "401(k)") },
  { value: "403b", label: L("403(b)", "403(b)") },
  { value: "hsa", label: L("HSA", "HSA") },
  { value: "529", label: L("529 Plan", "529 Plan") },
  { value: "checking", label: L("Compte courant", "Checking") },
  { value: "savings", label: L("Épargne", "Savings") },
  { value: "cd", label: L("CD (dépôt à terme)", "CD") },
  { value: "money_market", label: L("Money Market", "Money Market") },
  { value: "other", label: L("Autre / produit bancaire", "Other / bank product") },
];

export function accountTypesFor(country) {
  return country === "US" ? US_TYPES : FR_TYPES;
}

// Security asset classes (drive the donut + holdings class filter).
export const SECURITY_CLASSES = [
  { key: "stocks", color: "#6366F1", label: L("Actions", "Stocks") },
  { key: "etf", color: "#0EA5E9", label: L("ETF", "ETFs") },
  { key: "fund", color: "#F472B6", label: L("Fonds", "Funds") },
  { key: "bond", color: "#2DD4BF", label: L("Obligations", "Bonds") },
  { key: "option", color: "#FB923C", label: L("Options", "Options") },
];

// Map a holding's asset_type ("stock"/"etf"/… or "Stock") → a donut class key.
export function holdingClassKey(type) {
  const v = (type || "stock").toString().toLowerCase();
  if (v === "stock") return "stocks";
  return ["etf", "fund", "bond", "option"].includes(v) ? v : "stocks";
}

export function securityClassMeta(type) {
  const key = holdingClassKey(type);
  return SECURITY_CLASSES.find((c) => c.key === key) || SECURITY_CLASSES[0];
}

export function accountTypeLabel(type, customLabel, country, lang) {
  if (type === "other" && customLabel) return customLabel;
  const list = [...FR_TYPES, ...US_TYPES];
  const found = list.find((x) => x.value === type);
  return found ? (lang === "fr" ? found.label.fr : found.label.en) : (type || "—");
}

// Activity ledger types (Wealthfolio-style)
export const ACTIVITY_TYPES = [
  { value: "buy", label: L("Achat", "Buy") },
  { value: "sell", label: L("Vente", "Sell") },
  { value: "deposit", label: L("Dépôt", "Deposit") },
  { value: "withdrawal", label: L("Retrait", "Withdrawal") },
  { value: "dividend", label: L("Dividende", "Dividend") },
  { value: "transfer", label: L("Transfert", "Transfer") },
  { value: "split", label: L("Division", "Split") },
  { value: "fee", label: L("Frais", "Fee") },
  { value: "interest", label: L("Intérêts", "Interest") },
  { value: "tax", label: L("Impôt", "Tax") },
];

export const TRADE_TYPES = ["buy", "sell"];
export const CASH_TYPES = ["deposit", "withdrawal", "dividend", "transfer", "fee", "interest", "tax"];

export function activityTypeLabel(type, lang) {
  const found = ACTIVITY_TYPES.find((x) => x.value === type);
  return found ? (lang === "fr" ? found.label.fr : found.label.en) : (type || "—");
}

// Per-type accent colors for the activity pills (Wealthfolio-style).
export const ACTIVITY_COLORS = {
  buy: "#16A34A",
  sell: "#DC2626",
  dividend: "#84CC16",
  interest: "#14B8A6",
  deposit: "#3B82F6",
  withdrawal: "#F97316",
  transfer: "#6366F1",
  split: "#A855F7",
  fee: "#64748B",
  tax: "#B45309",
};

export function activityColor(type) {
  return ACTIVITY_COLORS[type] || "#64748B";
}

export const ASSET_TYPES = [
  { value: "stock", label: L("Action", "Stock") },
  { value: "etf", label: L("ETF", "ETF") },
  { value: "fund", label: L("Fonds", "Fund") },
  { value: "option", label: L("Option", "Option") },
  { value: "bond", label: L("Obligation", "Bond") },
];

export const DIVIDEND_TYPES = [
  { value: "cash", label: L("Espèces", "Cash") },
  { value: "drip", label: L("Réinvesti (DRIP)", "DRIP") },
  { value: "in_kind", label: L("En nature", "In kind") },
];

export const INTEREST_TYPES = [
  { value: "cash", label: L("Espèces", "Cash") },
  { value: "staking", label: L("Récompense staking", "Staking reward") },
];

export const OPTION_SIDES = [
  { value: "call", label: L("Call", "Call") },
  { value: "put", label: L("Put", "Put") },
];

export const TRANSFER_KINDS = [
  { value: "cash", label: L("Espèces", "Cash") },
  { value: "securities", label: L("Titres", "Securities") },
];
